import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import PullToRefresh from '@/components/PullToRefresh';
import { 
  ChevronRight, 
  Users,
  Trophy,
  Newspaper,
  MessageSquare,
  Building2,
  BarChart3,
  RefreshCw,
  Bell,
  AlertTriangle,
  TrendingDown,
  Minus,
  TrendingUp,
  Sparkles,
  X,
  ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import CityHeadDashboard from './CityHeadDashboard';
import { UserAvatar } from '@/components/UserAvatar';
import { MobileMenuTrigger } from '@/components/Layout';

// SAIRA Intro Banner Component
const SAIRAIntroBanner = ({ onTrySaira, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onDismiss();
    }, 300);
  };

  const handleTrySaira = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      onTrySaira();
    }, 200);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed bottom-24 left-4 right-4 z-40 transition-all duration-300 ${
        isExiting ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'
      }`}
      data-testid="saira-intro-banner"
    >
      <Card className="bg-gradient-to-r from-purple-600 to-indigo-600 border-0 shadow-xl rounded-2xl overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles size={24} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-white text-base flex items-center gap-1">
                Meet SAIRA 👋
              </h3>
              <p className="text-white/90 text-sm mt-1 leading-relaxed">
                I'm SAIRA — Sales AI Realestate Advisor. I'm your 24/7 sales coach to help you close more deals.
              </p>
            </div>
            <button 
              onClick={handleDismiss}
              className="p-1 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="saira-intro-dismiss"
            >
              <X size={18} className="text-white/70" />
            </button>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleTrySaira}
              className="flex-1 bg-white text-purple-700 hover:bg-white/90 font-semibold h-10 rounded-xl"
              data-testid="saira-intro-try"
            >
              <Sparkles size={16} className="mr-2" />
              Try SAIRA
            </Button>
            <Button
              variant="ghost"
              onClick={handleDismiss}
              className="text-white/80 hover:text-white hover:bg-white/10 h-10 px-4 rounded-xl"
              data-testid="saira-intro-dismiss-btn"
            >
              Dismiss
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

// Check if user is admin role
const isAdminRole = (role) => {
  return ['Admin', 'super_admin', 'city_admin', 'strategy_support_admin'].includes(role);
};

const Dashboard = ({ user }) => {
  // If admin user, show Sales Intelligence Dashboard (CityHeadDashboard)
  if (isAdminRole(user?.role)) {
    return <CityHeadDashboard user={user} />;
  }
  
  // Rep Dashboard (redesigned)
  return <RepDashboard user={user} />;
};

const RepDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [marketPulse, setMarketPulse] = useState(null);
  const [taskStats, setTaskStats] = useState({ open_tasks: 0, due_today: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [showSairaIntro, setShowSairaIntro] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchTaskStats();
    checkSairaIntroSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-hide banner after 5 seconds
  useEffect(() => {
    if (showSairaIntro) {
      const timer = setTimeout(() => {
        handleDismissSairaIntro();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSairaIntro]);

  const fetchTaskStats = async () => {
    try {
      const response = await api.getMyTasksSummary();
      setTaskStats(response.data);
    } catch (error) {
      console.error('Failed to load task stats:', error);
    }
  };

  const checkSairaIntroSeen = async () => {
    try {
      // First check localStorage for quick response
      const localKey = `saira_intro_seen_${user?.user_id}`;
      const localSeen = localStorage.getItem(localKey);
      
      if (localSeen === 'true') {
        setShowSairaIntro(false);
        return;
      }

      // Then check backend
      const response = await api.getUserPreferences();
      if (response.data?.saira_intro_seen) {
        localStorage.setItem(localKey, 'true');
        setShowSairaIntro(false);
      } else {
        setShowSairaIntro(true);
      }
    } catch (error) {
      // If API fails, check localStorage only
      const localKey = `saira_intro_seen_${user?.user_id}`;
      if (localStorage.getItem(localKey) !== 'true') {
        setShowSairaIntro(true);
      }
    }
  };

  const markSairaIntroSeen = async () => {
    const localKey = `saira_intro_seen_${user?.user_id}`;
    localStorage.setItem(localKey, 'true');
    
    try {
      await api.updateUserPreferences({ saira_intro_seen: true });
    } catch (error) {
      console.error('Failed to save preference to backend:', error);
    }
  };

  const handleTrySaira = () => {
    markSairaIntroSeen();
    setShowSairaIntro(false);
    navigate('/saira');
  };

  const handleDismissSairaIntro = () => {
    markSairaIntroSeen();
    setShowSairaIntro(false);
  };

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      // Fetch dashboard data, market pulse, and CRM stats in parallel
      const [dashboardResponse, pulseResponse, crmStatsResponse] = await Promise.all([
        api.getIndividualDashboard(),
        api.getMarketPulse().catch(() => ({ data: null })),
        api.getCRMStats().catch(() => ({ data: null }))
      ]);
      
      // Merge CRM stats into dashboard data
      const mergedData = {
        ...dashboardResponse.data,
        today_followups: crmStatsResponse.data?.today_followups || 0
      };
      setData(mergedData);
      setMarketPulse(pulseResponse.data);
    } catch (error) {
      console.error('Dashboard load error:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    try {
      const [dashboardResponse, pulseResponse, crmStatsResponse] = await Promise.all([
        api.getIndividualDashboard(),
        api.getMarketPulse().catch(() => ({ data: null })),
        api.getCRMStats().catch(() => ({ data: null }))
      ]);
      const mergedData = {
        ...dashboardResponse.data,
        today_followups: crmStatsResponse.data?.today_followups || 0
      };
      setData(mergedData);
      setMarketPulse(pulseResponse.data);
      toast.success('Dashboard refreshed');
    } catch (error) {
      toast.error('Refresh failed');
    }
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getUserFirstName = () => {
    const name = user?.rep_name || user?.full_name || user?.email?.split('@')[0] || 'there';
    const firstName = name.split(' ')[0];
    return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)}L`;
    }
    return `₹${amount?.toLocaleString() || 0}`;
  };

  // Market mood display
  const getMoodDisplay = () => {
    if (!marketPulse?.sentiment) return { text: 'Live', color: 'text-slate-600', icon: Minus, bg: 'bg-slate-100' };
    const sentiment = marketPulse.sentiment;
    if (sentiment === 'Positive') return { text: 'Positive', color: 'text-emerald-700', icon: TrendingUp, bg: 'bg-emerald-100' };
    if (sentiment === 'Risk') return { text: 'Risk', color: 'text-red-700', icon: TrendingDown, bg: 'bg-red-100' };
    return { text: 'Monitor', color: 'text-amber-700', icon: AlertTriangle, bg: 'bg-amber-100' };
  };

  const moodInfo = getMoodDisplay();

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-[#F8FAFC]">
        <div className="px-4 pt-4 pb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="h-7 w-44 bg-slate-200 rounded-lg animate-pulse mb-2"></div>
              <div className="h-4 w-28 bg-slate-200 rounded animate-pulse"></div>
            </div>
            <div className="flex gap-2">
              <div className="h-10 w-10 bg-slate-200 rounded-full animate-pulse"></div>
              <div className="h-10 w-10 bg-slate-200 rounded-full animate-pulse"></div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-[140px] bg-slate-200 rounded-2xl animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Standardized Card Styles - All cards same dimensions with tinted backgrounds
  const cardStyles = {
    dashboard: {
      bg: 'bg-slate-50',
      iconBg: 'bg-slate-200',
      iconColor: 'text-slate-600',
      textColor: 'text-slate-800'
    },
    crm: {
      bg: 'bg-purple-50',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      textColor: 'text-purple-900'
    },
    leaderboard: {
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      textColor: 'text-amber-900'
    },
    intel: {
      bg: 'bg-blue-50',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      textColor: 'text-blue-900'
    },
    feed: {
      bg: 'bg-teal-50',
      iconBg: 'bg-teal-100',
      iconColor: 'text-teal-600',
      textColor: 'text-teal-900'
    },
    projects: {
      bg: 'bg-emerald-50',
      iconBg: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
      textColor: 'text-emerald-900'
    }
  };

  // Action Grid Cards - All standardized
  const actionCards = [
    {
      id: 'dashboard',
      label: 'My Dashboard',
      icon: BarChart3,
      path: '/performance',
      style: cardStyles.dashboard,
      description: 'Performance & KPIs',
      stat: data?.approved_net_sales ? formatCurrency(data.approved_net_sales) : null,
      statLabel: 'Approved'
    },
    {
      id: 'crm',
      label: 'CRM',
      icon: Users,
      path: '/crm',
      style: cardStyles.crm,
      description: 'Customer pipeline',
      stat: data?.active_leads || null,
      statLabel: 'Active leads'
    },
    {
      id: 'leaderboard',
      label: 'Leaderboard',
      icon: Trophy,
      path: '/leaderboard',
      style: cardStyles.leaderboard,
      description: 'Rankings',
      stat: data?.rank ? `#${data.rank}` : null,
      statLabel: 'Your rank'
    },
    {
      id: 'intel',
      label: 'Market Intel',
      icon: Newspaper,
      path: '/market-intelligence',
      style: cardStyles.intel,
      description: 'Daily brief',
      customContent: (
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${moodInfo.bg} ${moodInfo.color}`}>
            {moodInfo.text}
          </span>
          {marketPulse?.article_count > 0 && (
            <span className="text-xs text-blue-600">{marketPulse.article_count} new</span>
          )}
        </div>
      )
    },
    {
      id: 'feed',
      label: 'Feed',
      icon: MessageSquare,
      path: '/feed',
      style: cardStyles.feed,
      description: 'Team updates',
    },
    {
      id: 'projects',
      label: 'Projects',
      icon: Building2,
      path: '/projects',
      style: cardStyles.projects,
      description: 'Property listings',
    },
  ];

  return (
    <div className="h-[100dvh] bg-[#F8FAFC] page-fade-in" data-testid="rep-home">
      {/* Header - Clean with hamburger */}
      <div className="bg-white border-b border-slate-100 px-4 py-3 md:px-8 header-blur sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-[#111827]">
              {getGreeting()}, {getUserFirstName()}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Sales Rep • {user?.city || 'India'}
            </p>
          </div>
          
          <div className="flex items-center gap-1">
            <button 
              onClick={() => navigate('/notifications')}
              className="p-2.5 rounded-full hover:bg-slate-100 transition-colors duration-200 relative"
              data-testid="header-notifications"
            >
              <Bell size={22} className="text-slate-600" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>
            <button 
              onClick={() => navigate('/profile')}
              className="p-1.5 rounded-full hover:bg-slate-100 transition-colors duration-200"
              data-testid="header-profile"
            >
              <UserAvatar 
                name={user?.rep_name || user?.email}
                imageUrl={user?.profile_image_url}
                size="sm"
              />
            </button>
            {/* Hamburger Menu */}
            <MobileMenuTrigger user={user} />
          </div>
        </div>
      </div>

      {/* Main Content - Wrapped in PullToRefresh */}
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="px-4 py-4 md:px-8 pb-24">
          <div className="max-w-[1400px] mx-auto">
            {/* 2x3 Action Grid - Tighter spacing */}
            <div className="grid grid-cols-2 gap-2.5 md:gap-4" data-testid="action-grid">
            {actionCards.map((card) => {
              const Icon = card.icon;
              const style = card.style;
              
              return (
                <button
                  key={card.id}
                  data-testid={`action-card-${card.id}`}
                  onClick={() => navigate(card.path)}
                  className={`relative overflow-hidden rounded-2xl p-4 text-left transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] ${style.bg} border border-slate-100`}
                  style={{ minHeight: '140px' }}
                >
                  {/* Arrow - Top Right, Non-overlapping */}
                  <div className={`absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center ${style.iconBg}`}>
                    <ChevronRight size={16} className={style.iconColor} />
                  </div>
                  
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${style.iconBg}`}>
                    <Icon size={20} className={style.iconColor} strokeWidth={2} />
                  </div>
                  
                  {/* Label */}
                  <h3 className={`text-base font-bold mb-0.5 ${style.textColor}`}>
                    {card.label}
                  </h3>
                  
                  {/* Description */}
                  <p className="text-xs text-slate-500 mb-2">
                    {card.description}
                  </p>
                  
                  {/* Custom content or stat */}
                  {card.customContent ? (
                    card.customContent
                  ) : card.stat && (
                    <div className="flex items-baseline gap-1.5">
                      <span className={`text-lg font-bold ${style.textColor}`}>
                        {card.stat}
                      </span>
                      <span className="text-xs text-slate-400">
                        {card.statLabel}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Today's Focus Section - Mobile */}
          <div className="mt-4 md:hidden">
            <h2 className="text-sm font-semibold text-slate-600 mb-2">Today's Focus</h2>
            <div className="grid grid-cols-2 gap-2.5">
              {/* Follow-ups Card */}
              <button
                onClick={() => navigate('/crm?followups_due_today=true')}
                className="bg-white rounded-xl p-3 text-left border border-slate-100 hover:border-slate-200 transition-colors btn-press"
                data-testid="followups-today-card"
              >
                <div className="text-2xl font-bold text-slate-800">
                  {data?.today_followups || 0}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {(data?.today_followups || 0) === 0 ? 'All done ✓' : 'Follow-ups Today'}
                </div>
              </button>
              
              {/* Pending Bookings Card */}
              <button
                onClick={() => navigate('/my-bookings?status=pending')}
                className="bg-white rounded-xl p-3 text-left border border-slate-100 hover:border-slate-200 transition-colors btn-press"
                data-testid="pending-bookings-card"
              >
                <div className="text-2xl font-bold text-slate-800">
                  {data?.pending_count || 0}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {(data?.pending_count || 0) === 0 ? 'No pending' : 'Pending Bookings'}
                </div>
              </button>
            </div>
          </div>

          {/* Quick Stats Bar - Desktop Only */}
          {data && (
            <div className="hidden md:block mt-6 p-4 bg-white rounded-2xl border border-slate-200 shadow-sm" data-testid="quick-stats">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-6">
                  <div>
                    <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Approved</p>
                    <p className="text-lg font-bold text-emerald-600">{formatCurrency(data?.approved_net_sales || 0)}</p>
                  </div>
                  <div className="h-8 w-px bg-slate-200"></div>
                  <div>
                    <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Pending</p>
                    <p className="text-lg font-bold text-amber-600">{formatCurrency(data?.pending_net_sales || 0)}</p>
                  </div>
                  <div className="h-8 w-px bg-slate-200"></div>
                  <div>
                    <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Target</p>
                    <p className="text-lg font-bold text-slate-700">{formatCurrency(data?.target || 0)}</p>
                  </div>
                </div>
                <button
                  onClick={fetchDashboard}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors duration-200"
                  data-testid="refresh-stats"
                >
                  <RefreshCw size={18} className="text-slate-400" />
                </button>
              </div>
              
              {/* Progress bar */}
              {data?.target > 0 && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>Progress to target</span>
                    <span>{Math.min(100, Math.round(((data?.approved_net_sales || 0) / data.target) * 100))}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#5B3DF5] to-[#8B5CF6] rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(100, ((data?.approved_net_sales || 0) / data.target) * 100)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </PullToRefresh>
      
      {/* SAIRA Introduction Banner */}
      {showSairaIntro && (
        <SAIRAIntroBanner 
          onTrySaira={handleTrySaira}
          onDismiss={handleDismissSairaIntro}
        />
      )}
    </div>
  );
};

export default Dashboard;
